import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Upload, Sparkles, CheckCircle2 } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

export default function CreateRaffle() {
    const nav = useNavigate();
    const { user } = useAuth();
    const [form, setForm] = useState({
        title: "",
        description: "",
        prize: "",
        price_per_ticket: 5,
        total_tickets: 100,
        draw_date: "",
        yape_phone: "",
    });
    const [image, setImage] = useState(null);
    const [imageUrl, setImageUrl] = useState(null);
    const [qr, setQr] = useState(null);
    const [qrUrl, setQrUrl] = useState(null);
    const [uploadingImg, setUploadingImg] = useState(false);
    const [uploadingQr, setUploadingQr] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const imgRef = useRef(null);
    const qrRef = useRef(null);

    const upload = async (file, kind) => {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        return data.url;
    };

    const onImage = async (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        setImage(f);
        setUploadingImg(true);
        try {
            const url = await upload(f, "image");
            setImageUrl(url);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setUploadingImg(false);
        }
    };
    const onQR = async (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        setQr(f);
        setUploadingQr(true);
        try {
            const url = await upload(f, "qr");
            setQrUrl(url);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setUploadingQr(false);
        }
    };

    const submit = async (e) => {
        e.preventDefault();
        if (!imageUrl) {
            toast.error("Sube una imagen para tu rifa");
            return;
        }
        if (!qrUrl) {
            toast.error("Sube tu código QR Yape");
            return;
        }
        setSubmitting(true);
        try {
            await api.post("/raffles", {
                ...form,
                price_per_ticket: Number(form.price_per_ticket),
                total_tickets: Number(form.total_tickets),
                image_url: imageUrl,
                yape_qr_url: qrUrl,
                draw_date: new Date(form.draw_date).toISOString(),
            });
            toast.success(user?.role === "admin" ? "¡Rifa creada y publicada!" : "¡Rifa enviada! Esperando aprobación del admin.");
            nav("/dashboard");
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="max-w-3xl mx-auto px-4 py-12" data-testid="create-raffle-page">
            <div className="mb-8">
                <div className="text-xs uppercase tracking-[0.3em] font-bold opacity-60 mb-2">Nuevo lanzamiento</div>
                <h1 className="font-display font-black text-5xl uppercase">Crea tu rifa</h1>
                {user?.role !== "admin" && (
                    <p className="text-ink/70 mt-2 text-sm">
                        <Sparkles className="w-4 h-4 inline mr-1 text-coral" strokeWidth={3} />
                        Tu rifa será revisada por el administrador antes de publicarse.
                    </p>
                )}
            </div>

            <form onSubmit={submit} className="brut-card p-6 space-y-5">
                <div>
                    <label className="brut-label" htmlFor="title">Título *</label>
                    <input id="title" required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="brut-input" placeholder="Sorteo iPhone 15" data-testid="raffle-title" />
                </div>

                <div>
                    <label className="brut-label" htmlFor="prize">Premio *</label>
                    <input id="prize" required value={form.prize} onChange={(e) => setForm({ ...form, prize: e.target.value })} className="brut-input" placeholder="iPhone 15 + S/ 500" data-testid="raffle-prize" />
                </div>

                <div>
                    <label className="brut-label" htmlFor="description">Descripción *</label>
                    <textarea id="description" required rows={4} minLength={10} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="brut-input" placeholder="Describe la rifa, condiciones, fecha del sorteo, etc." data-testid="raffle-description" />
                </div>

                <div className="grid md:grid-cols-3 gap-4">
                    <div>
                        <label className="brut-label" htmlFor="price">Precio boleto (S/) *</label>
                        <input id="price" type="number" min={0.5} step={0.5} required value={form.price_per_ticket} onChange={(e) => setForm({ ...form, price_per_ticket: e.target.value })} className="brut-input" data-testid="raffle-price" />
                    </div>
                    <div>
                        <label className="brut-label" htmlFor="total">Total boletos *</label>
                        <input id="total" type="number" min={10} max={10000} required value={form.total_tickets} onChange={(e) => setForm({ ...form, total_tickets: e.target.value })} className="brut-input" data-testid="raffle-total" />
                    </div>
                    <div>
                        <label className="brut-label" htmlFor="date">Fecha sorteo *</label>
                        <input id="date" type="date" required value={form.draw_date} onChange={(e) => setForm({ ...form, draw_date: e.target.value })} className="brut-input" data-testid="raffle-date" />
                    </div>
                </div>

                <div>
                    <label className="brut-label" htmlFor="yape_phone">Número Yape (opcional)</label>
                    <input id="yape_phone" value={form.yape_phone} onChange={(e) => setForm({ ...form, yape_phone: e.target.value })} className="brut-input" placeholder="987654321" data-testid="raffle-yape-phone" />
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                    <div>
                        <label className="brut-label">Imagen de la rifa *</label>
                        <input ref={imgRef} type="file" accept="image/*" onChange={onImage} className="hidden" data-testid="raffle-image-input" />
                        <button type="button" onClick={() => imgRef.current?.click()} className="brut-btn w-full" data-testid="raffle-image-btn">
                            {uploadingImg ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" strokeWidth={3} />}
                            {image ? "Cambiar imagen" : "Subir imagen"}
                        </button>
                        {imageUrl && (
                            <div className="mt-2 flex items-center gap-2 text-xs text-green-700 font-bold">
                                <CheckCircle2 className="w-4 h-4" /> Imagen subida
                            </div>
                        )}
                    </div>
                    <div>
                        <label className="brut-label">Código QR Yape *</label>
                        <input ref={qrRef} type="file" accept="image/*" onChange={onQR} className="hidden" data-testid="raffle-qr-input" />
                        <button type="button" onClick={() => qrRef.current?.click()} className="brut-btn w-full" data-testid="raffle-qr-btn">
                            {uploadingQr ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" strokeWidth={3} />}
                            {qr ? "Cambiar QR" : "Subir QR Yape"}
                        </button>
                        {qrUrl && (
                            <div className="mt-2 flex items-center gap-2 text-xs text-green-700 font-bold">
                                <CheckCircle2 className="w-4 h-4" /> QR subido
                            </div>
                        )}
                    </div>
                </div>

                <button type="submit" disabled={submitting} className="brut-btn brut-btn-primary w-full" data-testid="submit-raffle-btn">
                    {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Publicar rifa"}
                </button>
            </form>
        </div>
    );
}
